/**
 * The rule /get-started builds before there is an account. Once the visitor
 * has signed up or in on that page, the dashboard sends it to
 * POST /api/alerts/signup-rule (web/src/handoff.ts), which saves it switched
 * off until the plan has alerts (AlertService.saveSignupRule, fitToPlan).
 * Shared so the page's bundle, the dashboard and the server read one shape.
 */

import {
  BROAD_SECTORS,
  LIVE_CHANNELS,
  RULE_REGIONS,
  type BroadSector,
  type Channel,
  type LeadTime,
  type RuleRegion,
  type RuleSpec,
} from './rule';

/** When the page's rule alerts, in the order its chips and sentence go. */
export const MOMENTS = [
  'launch',
  '7d',
  '72h',
  '24h',
  'near_cap',
  'perk_expiry',
] as const;
export type Moment = (typeof MOMENTS)[number];

/** Each moment as the rule's lead time. */
export const MOMENT_LEADS: Record<Moment, LeadTime> = {
  launch: 'INSTANT',
  '7d': '7D',
  '72h': '72H',
  '24h': '24H',
  near_cap: 'NEAR_CAP',
  perk_expiry: 'PERK_EXPIRY',
};

/** "Minimum ticket" past "Any", in euros. Each is one of TICKET_STEPS. */
export const SIGNUP_TICKETS_EUR = [100, 500, 1000] as const;
export type SignupTicket = (typeof SIGNUP_TICKETS_EUR)[number];

/** A channel as the payload spells it: "email". */
export type SignupChannel = Lowercase<Channel>;

export interface SignupRule {
  /**
   * Where the visitor said they live, an ISO code: who "open to me" means.
   * Null when they kept the page's guess, which is not saved, as the
   * dashboard's picker saves none.
   */
  country: string | null;
  /** "Only raises open to me". */
  onlyEligible: boolean;
  /** Empty: every region. */
  regions: RuleRegion[];
  /** Empty: every sector. */
  sectors: BroadSector[];
  /** The highest minimum ticket to hear about; null is "Any". */
  maxMinTicketEur: SignupTicket | null;
  /** Empty: no alerts, only the Weekly Sonar. */
  moments: Moment[];
  channels: SignupChannel[];
}

/** Every value of `raw` if it is a list of allowed values, else an error. */
function subset<T extends string>(
  raw: unknown,
  allowed: readonly T[],
  field: string,
): T[] | string {
  if (
    !Array.isArray(raw) ||
    raw.some((v) => !allowed.includes(v as T)) ||
    new Set(raw).size !== raw.length
  ) {
    return `${field} must be a list of ${allowed.join(', ')}.`;
  }
  return raw as T[];
}

const CHANNEL_NAMES = LIVE_CHANNELS.map(
  (c) => c.toLowerCase() as SignupChannel,
);

/** The page's rule from a request body, or what is wrong with it. */
export function parseSignupRule(
  raw: unknown,
): { rule: SignupRule } | { error: string } {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { error: 'A rule must be an object.' };
  }
  const r = raw as Record<string, unknown>;
  if (
    r.country !== null &&
    (typeof r.country !== 'string' || !/^[A-Z]{2}$/.test(r.country))
  ) {
    return { error: 'country must be an ISO 3166-1 alpha-2 code, or null.' };
  }
  if (typeof r.onlyEligible !== 'boolean') {
    return { error: 'onlyEligible must be true or false.' };
  }
  const regions = subset(r.regions, RULE_REGIONS, 'regions');
  if (typeof regions === 'string') return { error: regions };
  const sectors = subset(r.sectors, BROAD_SECTORS, 'sectors');
  if (typeof sectors === 'string') return { error: sectors };
  const ticket = r.maxMinTicketEur;
  if (ticket !== null && !SIGNUP_TICKETS_EUR.includes(ticket as SignupTicket)) {
    return {
      error: `maxMinTicketEur must be ${SIGNUP_TICKETS_EUR.join(', ')} or null.`,
    };
  }
  const moments = subset(r.moments, MOMENTS, 'moments');
  if (typeof moments === 'string') return { error: moments };
  if (!Array.isArray(r.channels)) {
    return { error: 'channels must be a list.' };
  }
  if (r.channels.some((c) => !CHANNEL_NAMES.includes(c as SignupChannel))) {
    return { error: 'Only email alerts are available for now.' };
  }
  return {
    rule: {
      country: r.country,
      onlyEligible: r.onlyEligible,
      regions,
      sectors,
      maxMinTicketEur: ticket as SignupTicket | null,
      moments,
      channels: r.channels as SignupChannel[],
    },
  };
}

/**
 * The rule the page built, as the Alerts centre would save it: quiet
 * overnight on the visitor's clock, as the builder's new rule is, and by
 * email when no channel was picked, as the page's sentence says. Null when
 * no moment was picked: that asks for the Weekly Sonar only, which is no
 * rule at all.
 */
export function signupRuleSpec(
  rule: SignupRule,
  timeZone: string,
): RuleSpec | null {
  if (rule.moments.length === 0) return null;
  const channels = rule.channels.map((c) => c.toUpperCase() as Channel);
  return {
    regions: rule.regions,
    sectors: rule.sectors,
    maxTicket: rule.maxMinTicketEur,
    ticketCurrency: 'EUR',
    openToMe: rule.onlyEligible,
    leadTimes: MOMENTS.filter((m) => rule.moments.includes(m)).map(
      (m) => MOMENT_LEADS[m],
    ),
    channels: channels.length ? channels : ['EMAIL'],
    quietHours: { from: 22, until: 7 },
    timeZone,
  };
}
